import { MemoryFileSystem, ZipFileSystem, ZipReadFileSystem } from '@playcanvas/splat-transform';

import { ElementType } from './element';
import { Events } from './events';
import { BrowserFileSystem, BlobReadSource } from './io';
import { Model } from './model';
import { recentFiles } from './recent-files';
import { Scene } from './scene';
import { createSceneObject, SceneObject } from './scene-object';
import { Splat } from './splat';
import { serializePly } from './splat-serialize';
import { Transform } from './transform';
import { localize } from './ui/localization';

// ts compiler and vscode find this type, but eslint does not
type FilePickerAcceptType = unknown;

const SuperFileType: FilePickerAcceptType[] = [{
    description: 'SuperSplat document',
    accept: {
        'application/x-supersplat': ['.ssproj']
    }
}];

type FileSelectorCallback = (fileList: File) => void;

// helper class to show a file selector dialog.
// used when showOpenFilePicker is not available.
class FileSelector {
    show: (callbackFunc: FileSelectorCallback) => void;

    constructor() {
        const fileSelector = document.createElement('input');
        fileSelector.setAttribute('id', 'document-file-selector');
        fileSelector.setAttribute('type', 'file');
        fileSelector.setAttribute('accept', '.ssproj');
        fileSelector.setAttribute('multiple', 'false');

        document.body.append(fileSelector);

        let callbackFunc: FileSelectorCallback = null;

        fileSelector.addEventListener('change', () => {
            callbackFunc(fileSelector.files[0]);
        });

        fileSelector.addEventListener('cancel', () => {
            callbackFunc(null);
        });

        this.show = (func: FileSelectorCallback) => {
            callbackFunc = func;
            fileSelector.click();
        };
    }
}

const registerDocEvents = (scene: Scene, events: Events) => {
    // construct the file selector
    const fileSelector = window.showOpenFilePicker ? null : new FileSelector();

    // this file handle is updated as the current document is loaded and saved
    let documentFileHandle: FileSystemFileHandle = null;

    // show the user a reset confirmation popup
    const getResetConfirmation = async () => {
        const result = await events.invoke('showPopup', {
            type: 'yesno',
            header: localize('doc.reset'),
            message: localize(events.invoke('scene.dirty') ? 'doc.unsaved-message' : 'doc.reset-message')
        });

        if (result.action !== 'yes') {
            return false;
        }

        return true;
    };

    // reset the scene
    const resetScene = () => {
        // Leave Render mode before the new/loaded document's own view settings are applied, so
        // the stale Render-mode snapshot doesn't get restored over them on a later switch back.
        if (events.invoke('workspace.mode') === 'render') {
            events.fire('workspace.setMode', 'edit');
        }

        events.fire('scene.clear');
        events.fire('camera.reset');
        events.fire('doc.setName', null);
        documentFileHandle = null;
    };

    // load the document from the given file
    const loadDocument = async (file: File) => {
        events.fire('startSpinner');

        // Create streaming ZIP reader from the file
        const blobSource = new BlobReadSource(file);
        const zipFs = new ZipReadFileSystem(blobSource);

        try {
            // reset the scene
            resetScene();

            // read document.json via streaming (only reads what's needed)
            const docSource = await zipFs.createSource('document.json');
            const docData = await docSource.read().readAll();
            docSource.close();
            const document = JSON.parse(new TextDecoder().decode(docData));

            // run through each splat and load it
            for (let i = 0; i < document.splats.length; ++i) {
                const filename = `splat_${i}.ply`;
                const splatSettings = document.splats[i];

                // load splat directly from the zip filesystem (streams on-demand)
                // skipReorder=true because ssproj PLY files are already in morton order
                const splat = await scene.assetLoader.load(filename, zipFs, false, true);

                await scene.add(splat);

                splat.docDeserialize(splatSettings);
            }

            // run through each imported model (glB/glTF) and load it
            const modelDocs = document.models ?? [];
            for (let i = 0; i < modelDocs.length; ++i) {
                const modelSettings = modelDocs[i];

                // read the model binary from the zip
                const modelSource = await zipFs.createSource(modelSettings.file);
                const modelData = await modelSource.read().readAll();
                modelSource.close();

                const file = new File([new Uint8Array(modelData)], modelSettings.name);
                const model = await scene.assetLoader.loadModel(file, modelSettings.name);

                await scene.add(model);

                model.docDeserialize(modelSettings);

                // documents written before units were captured have no unit field
                model.unit = modelSettings.unit ?? null;
            }

            // run through each procedural content object (image/text/shape) and rebuild it
            const objectDocs = document.objects ?? [];
            for (let i = 0; i < objectDocs.length; ++i) {
                const objectSettings = objectDocs[i];
                const obj = await createSceneObject(scene, objectSettings.object, objectSettings.name);
                await scene.add(obj);
                obj.docDeserialize(objectSettings);
            }

            // FIXME: trigger scene bound calc in a better way
            const tmp = scene.bound;
            if (tmp === null) {
                console.error('this should never fire');
            }

            events.invoke('docDeserialize.timeline', document.timeline);
            events.invoke('docDeserialize.annotations', document.annotations);
            events.invoke('docDeserialize.environment', document.environment);
            await events.invoke('docDeserialize.water', document.water);
            events.invoke('docDeserialize.objectTracks', document.objectTracks);
            events.invoke('docDeserialize.poseSets', document.poseSets, document.camera?.fov);
            events.invoke('docDeserialize.view', document.view);
            scene.camera.docDeserialize(document.camera);

            // refresh the pivot to reflect the loaded transform
            const currentSelection = events.invoke('selection');
            if (currentSelection) {
                const pivot = events.invoke('pivot');
                const transform = new Transform();
                const pivotOrigin = events.invoke('pivot.origin');
                currentSelection.getPivot(pivotOrigin, false, transform);
                pivot.place(transform);
            }
        } catch (error) {
            await events.invoke('showPopup', {
                type: 'error',
                header: localize('doc.load-failed'),
                message: `'${error.message ?? error}'`
            });
        } finally {
            // Clean up resources
            zipFs.close();
            events.fire('stopSpinner');
        }
    };

    // serialize the whole scene into the given (already-open) zip filesystem.
    // shared by saving to a file and serializing to an in-memory buffer (for publish).
    const writeDocumentZip = async (zipFs: ZipFileSystem) => {
        const splats = events.invoke('scene.allSplats') as Splat[];
        const allModels = scene.getElementsByType(ElementType.model);
        // imported glB/glTF models (have original file bytes) vs procedural content objects
        const models = allModels.filter(m => m instanceof Model) as Model[];
        const objects = allModels.filter(m => m instanceof SceneObject) as SceneObject[];

        // extract a file extension (defaulting to .glb) for the stored model binary
        const getExt = (name: string) => {
            const idx = name.lastIndexOf('.');
            return idx >= 0 ? name.substring(idx) : '.glb';
        };

        const document = {
            version: 0,
            camera: scene.camera.docSerialize(),
            view: events.invoke('docSerialize.view'),
            poseSets: events.invoke('docSerialize.poseSets'),
            timeline: events.invoke('docSerialize.timeline'),
            splats: splats.map(s => s.docSerialize()),
            models: models.map((m, i) => ({
                ...m.docSerialize(),
                file: `model_${i}${getExt(m.name)}`,
                unit: m.unit ?? null
            })),
            objects: objects.map(o => o.docSerialize()),
            annotations: events.invoke('docSerialize.annotations'),
            environment: events.invoke('docSerialize.environment'),
            water: events.invoke('docSerialize.water'),
            objectTracks: events.invoke('docSerialize.objectTracks')
        };

        const serializeSettings = {
            // even though we support saving selection state, we disable that for now
            // because including a uint8 array in the document PLY results in slow loading
            // path.
            keepStateData: false,
            keepWorldTransform: true,
            keepColorTint: true
        };

        // Write document.json
        const docWriter = await zipFs.createWriter('document.json');
        await docWriter.write(new TextEncoder().encode(JSON.stringify(document)));
        await docWriter.close();

        // Write each splat as PLY
        for (let i = 0; i < splats.length; ++i) {
            await serializePly([splats[i]], serializeSettings, zipFs, `splat_${i}.ply`);
        }

        // Write each imported model's original glB/glTF binary
        for (let i = 0; i < models.length; ++i) {
            const modelWriter = await zipFs.createWriter(document.models[i].file);
            await modelWriter.write(models[i].fileData);
            await modelWriter.close();
        }

        // Close zip (also closes underlying writer)
        await zipFs.close();
    };

    const saveDocument = async (options: { stream?: FileSystemWritableFileStream, filename?: string }) => {
        events.fire('startSpinner');

        try {
            // Create browser filesystem and zip filesystem
            const browserFs = new BrowserFileSystem(options.filename, options.stream);
            const browserWriter = await browserFs.createWriter(options.filename);
            const zipFs = new ZipFileSystem(browserWriter);
            await writeDocumentZip(zipFs);
        } catch (error) {
            await events.invoke('showPopup', {
                type: 'error',
                header: localize('doc.save-failed'),
                message: `'${error.message ?? error}'`
            });
        } finally {
            events.fire('stopSpinner');
        }
    };

    // serialize the current scene to an in-memory .ssproj byte buffer (used by publish)
    events.function('doc.serializeToBytes', async (): Promise<Uint8Array> => {
        const memFs = new MemoryFileSystem();
        const memWriter = memFs.createWriter('scene.ssproj');
        const zipFs = new ZipFileSystem(memWriter);
        await writeDocumentZip(zipFs);
        return memFs.results.get('scene.ssproj');
    });

    // handle user requesting a new document
    events.function('doc.new', async () => {
        if (!await getResetConfirmation()) {
            return false;
        }
        resetScene();
        return true;
    });

    // handle document file being dropped
    // NOTE: on chrome it's possible to get the FileSystemFileHandle from the DataTransferItem
    // (which would result in more seamless user experience), but this is not yet supported in
    // other browsers.
    events.function('doc.load', async (file: File, handle?: FileSystemFileHandle) => {
        if (!events.invoke('scene.empty') && !await getResetConfirmation()) {
            return false;
        }

        await loadDocument(file);

        events.fire('doc.setName', file.name);

        if (handle) {
            documentFileHandle = handle;
            recentFiles.add(handle);
        }
    });

    events.function('doc.open', async () => {
        if (!events.invoke('scene.empty') && !await getResetConfirmation()) {
            return false;
        }

        if (fileSelector) {
            fileSelector.show(async (file?: File) => {
                if (file) {
                    await loadDocument(file);
                }
            });
        } else {
            try {
                const fileHandles = await window.showOpenFilePicker({
                    id: 'SuperSplatDocumentOpen',
                    multiple: false,
                    types: SuperFileType
                });

                if (fileHandles?.length === 1) {
                    const fileHandle = fileHandles[0];

                    // null file handle incase loadDocument fails
                    await loadDocument(await fileHandle.getFile());

                    // store file handle for subsequent saves
                    documentFileHandle = fileHandle;
                    events.fire('doc.setName', fileHandle.name);
                    recentFiles.add(fileHandle);
                }
            } catch (error) {
                if (error.name !== 'AbortError') {
                    console.error(error);
                }
            }
        }
    });

    events.function('doc.openRecent', async (fileHandle: FileSystemFileHandle) => {
        if (!events.invoke('scene.empty') && !await getResetConfirmation()) {
            return false;
        }

        try {
            if (await fileHandle.queryPermission({ mode: 'read' }) !== 'granted') {
                if (await fileHandle.requestPermission({ mode: 'read' }) !== 'granted') {
                    return false;
                }
            }

            await loadDocument(await fileHandle.getFile());

            // store file handle for subsequent saves
            documentFileHandle = fileHandle;
            events.fire('doc.setName', fileHandle.name);
            recentFiles.add(fileHandle);
        } catch (error) {
            if (error.name !== 'AbortError') {
                console.error(error);
                await events.invoke('showPopup', {
                    type: 'error',
                    header: localize('popup.error-loading'),
                    message: `${error.message ?? error}`
                });
            }
        }
    });

    events.function('doc.save', async () => {
        if (documentFileHandle) {
            try {
                await saveDocument({
                    stream: await documentFileHandle.createWritable()
                });
                events.fire('doc.saved');
            } catch (error) {
                if (error.name !== 'AbortError' && error.name !== 'NotAllowedError') {
                    console.error(error);
                }
            }
        } else {
            await events.invoke('doc.saveAs');
        }
    });

    events.function('doc.saveAs', async () => {
        if (window.showSaveFilePicker) {
            try {
                const handle = await window.showSaveFilePicker({
                    id: 'SuperSplatDocumentSave',
                    types: SuperFileType,
                    suggestedName: 'scene.ssproj'
                });
                await saveDocument({ stream: await handle.createWritable() });
                documentFileHandle = handle;
                events.fire('doc.setName', handle.name);
                events.fire('doc.saved');
                recentFiles.add(handle);
            } catch (error) {
                if (error.name !== 'AbortError') {
                    console.error(error);
                }
            }
        } else {
            await saveDocument({
                filename: 'scene.ssproj'
            });
            events.fire('doc.saved');
        }
    });

    // doc name

    let docName: string = null;

    const setDocName = (name: string) => {
        if (name !== docName) {
            docName = name;
            events.fire('doc.name', docName);
        }
    };

    events.function('doc.name', () => {
        return docName;
    });

    events.on('doc.setName', (name) => {
        setDocName(name);
    });
};

export { registerDocEvents };
